import { useState, useEffect, useMemo } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, apiFetch, errorMessage } from "./api";
import WalletPreview from "./WalletPreview";
import "./CardCustomization.css";

/* Nombre de tampons cochés dans l'aperçu (exemple montrant cochés et vides) */
const PREVIEW_STAMPS = 4;
const PREVIEW_POINTS = 120;

function CardCustomization({ user, onNavigate, onLogout }) {
  const [color, setColor] = useState("#cbb59a");
  const [cardMode, setCardMode] = useState("stamps");
  const [cardStyle, setCardStyle] = useState("kraft");
  const [rewardLabel, setRewardLabel] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [platform, setPlatform] = useState("google");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!user.organization_id) return;
    apiGet(`/api/organizations/${user.organization_id}/branding`)
      .then((data) => {
        if (data.color_primary) setColor(data.color_primary);
        if (data.card_mode) setCardMode(data.card_mode);
        if (data.card_style) setCardStyle(data.card_style);
        if (data.reward_label) setRewardLabel(data.reward_label);
        if (data.has_logo) {
          setLogoPreview(`/api/organizations/${user.organization_id}/logo?t=${Date.now()}`);
        }
        if (data.has_photo) {
          setPhotoPreview(`/api/organizations/${user.organization_id}/photo?t=${Date.now()}`);
        }
      })
      .catch((e) => {
        if (e.status === 403) setForbidden(true);
      });
  }, [user.organization_id]);

  /* Visuel (strip/hero) rendu par le même moteur que les vrais pass ; les
     uploads (logo, photo) sont prévisualisés côté client par WalletPreview */
  const previewUrl = useMemo(() => {
    if (!user.organization_id) return null;
    const params = new URLSearchParams({
      style: cardStyle,
      mode: cardMode,
      color,
      stamps: String(cardMode === "stamps" ? PREVIEW_STAMPS : 0),
    });
    return `/api/organizations/${user.organization_id}/card-preview.png?${params}`;
  }, [user.organization_id, cardStyle, cardMode, color]);

  if (!user.organization_id || forbidden) {
    return (
      <div className="cust-container">
        <GestionHeader
          user={user}
          onNavigate={onNavigate}
          title="Personnalisation"
          onBack={() => onNavigate("home")}
          onHome={() => onNavigate("home")}
          onLogout={onLogout}
        />
        <main className="cust-main">
          <p className="cust-error">
            {forbidden
              ? "La personnalisation des cartes est réservée aux plans Premium et Extra."
              : "Aucune organisation associée à votre compte."}
          </p>
        </main>
      </div>
    );
  }

  const handleFileChange = (setFile, setPreview) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFile(file);
    // Libère le blob précédent, sinon chaque essai d'image fuit en mémoire
    setPreview((prev) => {
      if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const photoMissing = cardStyle === "photo" && !photoFile && !photoPreview;

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    const formData = new FormData();
    formData.append("color_primary", color);
    formData.append("card_mode", cardMode);
    formData.append("card_style", cardStyle);
    formData.append("reward_label", rewardLabel);
    if (logoFile) {
      formData.append("logo", logoFile);
    }
    if (photoFile) {
      formData.append("photo", photoFile);
    }
    try {
      await apiFetch(`/api/organizations/${user.organization_id}/branding`,
        { method: "POST", body: formData }, "Erreur lors de l'enregistrement");
      setMessage("Personnalisation enregistrée — les cartes déjà en circulation "
        + "se mettent à jour automatiquement");
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de l'enregistrement"));
    }
    setSaving(false);
  };

  return (
    <div className="cust-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Personnalisation"
        onBack={() => onNavigate("home")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />
      <main className="cust-main">
        <div className="cust-layout">
          <div className="cust-form-section">
            <h2>Design de la carte</h2>

            <div className="cust-field">
              <label>Logo de l'entreprise</label>
              <div className="cust-logo-upload">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="cust-logo-preview" />
                ) : (
                  <div className="cust-logo-placeholder">Aucun logo</div>
                )}
                <label className="cust-upload-btn">
                  {logoPreview ? "Changer" : "Choisir un fichier"}
                  <input type="file" accept="image/*"
                         onChange={handleFileChange(setLogoFile, setLogoPreview)} hidden />
                </label>
              </div>
              <p className="cust-field-hint">
                Affiché en pastille ronde en haut de la carte. Sans logo,
                l'initiale du commerce est utilisée.
              </p>
            </div>

            <div className="cust-field">
              <label>Type de carte</label>
              <div className="cust-mode-row">
                <button
                  type="button"
                  className={`cust-mode-btn ${cardMode === "stamps" ? "active" : ""}`}
                  onClick={() => setCardMode("stamps")}
                >
                  Carte à tampons
                  <span>1 tampon par visite, récompense au bout de 10</span>
                </button>
                <button
                  type="button"
                  className={`cust-mode-btn ${cardMode === "points" ? "active" : ""}`}
                  onClick={() => setCardMode("points")}
                >
                  Carte à points
                  <span>montant libre de points à chaque passage</span>
                </button>
              </div>
            </div>

            <div className="cust-field">
              <label>Style du visuel</label>
              <div className="cust-mode-row">
                <button
                  type="button"
                  className={`cust-mode-btn ${cardStyle === "kraft" ? "active" : ""}`}
                  onClick={() => setCardStyle("kraft")}
                >
                  Papier kraft
                  <span>texture papier à la couleur de votre commerce</span>
                </button>
                <button
                  type="button"
                  className={`cust-mode-btn ${cardStyle === "photo" ? "active" : ""}`}
                  onClick={() => setCardStyle("photo")}
                >
                  Photo du commerce
                  <span>votre photo en bannière (carte à points)</span>
                </button>
              </div>
            </div>

            {cardStyle === "photo" && (
              <div className="cust-field">
                <label>Photo du commerce</label>
                <div className="cust-logo-upload">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Photo du commerce"
                         className="cust-photo-preview" />
                  ) : (
                    <div className="cust-logo-placeholder">Aucune photo</div>
                  )}
                  <label className="cust-upload-btn">
                    {photoPreview ? "Changer" : "Choisir un fichier"}
                    <input type="file" accept="image/*"
                           onChange={handleFileChange(setPhotoFile, setPhotoPreview)} hidden />
                  </label>
                </div>
                <p className="cust-field-hint">
                  {cardMode === "stamps"
                    ? "En carte à tampons, la grille prend la place de la photo — "
                      + "elle apparaîtra si vous basculez en carte à points."
                    : "Affichée en bannière sur la carte."}
                </p>
              </div>
            )}

            <div className="cust-field">
              <label htmlFor="rewardLabel">Récompense affichée</label>
              <input
                type="text"
                id="rewardLabel"
                className="cust-text-input"
                maxLength={60}
                placeholder="Ex. : un café offert au bout de 10 tampons"
                value={rewardLabel}
                onChange={(e) => setRewardLabel(e.target.value)}
              />
              <p className="cust-field-hint">
                Optionnel — sur Apple entre le visuel et le QR, sur Google dans
                les détails de la carte.
              </p>
            </div>

            <div className="cust-field">
              <label htmlFor="cardColor">Couleur de la carte</label>
              <div className="cust-color-row">
                <input
                  type="color"
                  id="cardColor"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
                <span className="cust-color-hex">{color}</span>
              </div>
            </div>

            <button className="cust-save-btn" onClick={handleSave}
                    disabled={saving || photoMissing}>
              {saving ? "Sauvegarde..." : "Sauvegarder"}
            </button>
            {photoMissing && (
              <p className="cust-field-hint">
                Ajoutez une photo du commerce pour enregistrer le style photo.
              </p>
            )}
            {/* Succès et erreur portés par deux états distincts : la détection
                par présence d'un « ! » dans le texte affichait des erreurs en vert. */}
            {message && <p className="cust-message success">{message}</p>}
            {error && <p className="cust-message error">{error}</p>}
          </div>

          <div className="cust-preview-section">
            <h2>Aperçu</h2>
            <div className="cust-platform-row">
              <button
                type="button"
                className={`cust-platform-btn ${platform === "google" ? "active" : ""}`}
                onClick={() => setPlatform("google")}
              >
                Android — Google Wallet
              </button>
              <button
                type="button"
                className={`cust-platform-btn ${platform === "apple" ? "active" : ""}`}
                onClick={() => setPlatform("apple")}
              >
                iPhone — Apple Wallet
              </button>
            </div>
            <WalletPreview
              platform={platform}
              orgName={user.organization}
              color={color}
              cardStyle={cardStyle}
              mode={cardMode}
              stamps={PREVIEW_STAMPS}
              points={PREVIEW_POINTS}
              niveau={rewardLabel}
              logoFile={logoFile}
              photoFile={photoFile}
              logoSrc={logoPreview}
              photoSrc={photoPreview}
              stripUrl={previewUrl}
              altText="N° de carte"
            />
            <p className="cust-preview-note">
              {cardMode === "stamps"
                ? "Carte à tampons : 1 tampon par passage, 10 tampons = 1 récompense. "
                  + "La grille se met à jour à chaque passage en caisse (en temps réel "
                  + "sur Google Wallet ; via notification sur Apple)."
                : "Carte à points : le compteur s'incrémente librement à chaque passage "
                  + "en caisse (en temps réel sur Google Wallet ; via notification "
                  + "sur Apple)."}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CardCustomization;
